'use client';

/**
 * Réduit son contenu pour qu'il tienne en LARGEUR (formules KaTeX : en mode
 * « display » elles ne passent jamais à la ligne et débordaient de la carte).
 * La taille de police est réduite proportionnellement (plancher `minScale`),
 * remesurée quand la place ou le contenu change (polices chargées, KaTeX).
 */

import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';

/** Échelle (PURE) pour qu'un contenu de largeur naturelle `natural` tienne dans `available`. */
export function fitScale(available: number, natural: number, minScale = 0.45): number {
  if (available <= 0 || natural <= 0) return 1;
  return Math.max(minScale, Math.min(1, available / natural));
}

export function FitWidth({
  children,
  minScale = 0.45,
  className,
}: {
  children: ReactNode;
  minScale?: number;
  className?: string;
}) {
  const outer = useRef<HTMLDivElement>(null);
  const inner = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const scaleRef = useRef(1);

  useLayoutEffect(() => {
    const o = outer.current;
    const i = inner.current;
    if (!o || !i) return;
    const update = () => {
      // Largeur mesurée à l'échelle courante → largeur naturelle (échelle 1).
      const natural = i.scrollWidth / scaleRef.current;
      const next = fitScale(o.clientWidth, natural, minScale);
      if (Math.abs(next - scaleRef.current) > 0.01) {
        scaleRef.current = next;
        setScale(next);
      }
    };
    update();
    const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(update);
    ro?.observe(o);
    ro?.observe(i);
    const mo = typeof MutationObserver === 'undefined' ? null : new MutationObserver(update);
    mo?.observe(i, { childList: true, subtree: true });
    if (typeof document !== 'undefined') void document.fonts?.ready.then(update);
    return () => {
      ro?.disconnect();
      mo?.disconnect();
    };
  }, [minScale]);

  return (
    <div
      ref={outer}
      className={`jr-tool-card-fit ${className ?? ''}`}
      data-scale={scale.toFixed(2)}
    >
      <div ref={inner} className="jr-tool-card-fit-inner" style={{ fontSize: `${scale}em` }}>
        {children}
      </div>
    </div>
  );
}
